import { Injectable } from '@angular/core';
import { Player, TeamComposition } from './player.model';

/**
 * Équilibrage en deux temps.
 *
 * 1. Draft en serpentin (A, B, B, A, ...) sur les joueurs triés par niveau.
 *    Contrairement à une distribution gloutonne pure, il garantit des effectifs
 *    égaux à un joueur près — indispensable pour un match jouable.
 * 2. Optimisation locale: on teste les échanges de joueurs entre les deux
 *    équipes et on garde ceux qui réduisent l'écart, à effectifs constants.
 *
 * Les ex aequo sont mélangés aléatoirement, donc deux appels successifs sur le
 * même effectif produisent des compositions différentes ("Regénérer").
 */
@Injectable({ providedIn: 'root' })
export class TeamBalancerService {
  private static readonly MAX_PASSES = 12;

  /**
   * `bench` n'entre pas dans l'équilibrage: il est simplement transporté dans
   * la composition pour être affiché et partagé avec les deux équipes.
   */
  generateBalancedTeams(players: Player[], bench: Player[] = []): TeamComposition {
    const pool = this.shuffle([...players]).sort((a, b) => b.rating - a.rating);

    const teamA: Player[] = [];
    const teamB: Player[] = [];

    // Serpentin: 0 -> A, 1 -> B, 2 -> B, 3 -> A, 4 -> A, ...
    pool.forEach((player, index) => {
      const goesToA = Math.floor(index / 2) % 2 === 0 ? index % 2 === 0 : index % 2 === 1;
      (goesToA ? teamA : teamB).push(player);
    });

    this.optimize(teamA, teamB);

    const teamAScore = this.sum(teamA);
    const teamBScore = this.sum(teamB);
    const total = teamAScore + teamBScore;

    return {
      teamA,
      teamB,
      bench: [...bench],
      metrics: {
        teamAScore,
        teamBScore,
        teamAAvg: this.average(teamA),
        teamBAvg: this.average(teamB),
        diff: Math.abs(teamAScore - teamBScore),
        balance: total === 0 ? 50 : Math.round((teamAScore / total) * 100)
      }
    };
  }

  /** Échanges deux à deux tant qu'ils réduisent l'écart. Mute les tableaux. */
  private optimize(teamA: Player[], teamB: Player[]): void {
    for (let pass = 0; pass < TeamBalancerService.MAX_PASSES; pass++) {
      let bestGain = 0;
      let bestPair: [number, number] | null = null;

      const diff = this.sum(teamA) - this.sum(teamB);
      if (diff === 0) return;

      for (let i = 0; i < teamA.length; i++) {
        for (let j = 0; j < teamB.length; j++) {
          // Échanger i et j déplace l'écart de 2 * (ratingA - ratingB).
          const newDiff = diff - 2 * (teamA[i].rating - teamB[j].rating);
          const gain = Math.abs(diff) - Math.abs(newDiff);
          if (gain > bestGain) {
            bestGain = gain;
            bestPair = [i, j];
          }
        }
      }

      if (!bestPair) return;
      const [i, j] = bestPair;
      [teamA[i], teamB[j]] = [teamB[j], teamA[i]];
    }
  }

  private sum(team: Player[]): number {
    return team.reduce((total, player) => total + player.rating, 0);
  }

  private average(team: Player[]): number {
    if (team.length === 0) return 0;
    return Math.round((this.sum(team) / team.length) * 10) / 10;
  }

  /** Fisher-Yates. */
  private shuffle<T>(list: T[]): T[] {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }
}
